import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  Plane,
  Heart,
  Coffee,
  ArrowRight,
  ArrowLeft,
  Check,
} from 'lucide-react';
import clsx from 'clsx';

// Step components will be created separately
import CoreBlock from './steps/CoreBlock';
import JourneyBlock from './steps/JourneyBlock';
import InterestsBlock from './steps/InterestsBlock';
import LifestyleBlock from './steps/LifestyleBlock';

const steps = [
  { key: 'core', title: 'Core Identity', subtitle: 'Your era, education, and generation', icon: GraduationCap, component: CoreBlock },
  { key: 'journey', title: 'Migration Journey', subtitle: 'Your path from India to abroad', icon: Plane, component: JourneyBlock },
  { key: 'interests', title: 'Interests & Hobbies', subtitle: 'What you enjoy doing', icon: Heart, component: InterestsBlock },
  { key: 'lifestyle', title: 'Lifestyle & Values', subtitle: 'How you connect with community', icon: Coffee, component: LifestyleBlock },
] as const;

export default function BoardCreate() {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const navigate = useNavigate();

  const handleNext = () => {
    setCompletedSteps((prev) => new Set([...prev, currentStep]));
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // All done!
      navigate('/lab/identity-board');
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (index: number) => {
    // Allow navigating to completed steps or the next available step
    if (index <= Math.max(...Array.from(completedSteps), -1) + 1) {
      setCurrentStep(index);
    }
  };

  const CurrentStepComponent = steps[currentStep].component;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="max-w-2xl mx-auto py-4">
      {/* Step Indicator */}
      <div className="flex items-center justify-between mb-8 px-4">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = completedSteps.has(index);
          const isAccessible = index <= Math.max(...Array.from(completedSteps), -1) + 1;

          return (
            <div
              key={step.key}
              className="flex items-center"
            >
              <button
                onClick={() => handleStepClick(index)}
                disabled={!isAccessible}
                className={clsx(
                  'flex items-center gap-2 p-2 rounded-lg transition-colors',
                  isAccessible && 'cursor-pointer',
                  !isAccessible && 'cursor-not-allowed opacity-50',
                  isActive && 'bg-primary-100',
                  isCompleted && !isActive && 'bg-twins-light'
                )}
              >
                <div
                  className={clsx(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    isActive && 'bg-primary-600 text-white',
                    isCompleted && !isActive && 'bg-twins text-white',
                    !isActive && !isCompleted && 'bg-gray-200 text-gray-500'
                  )}
                >
                  {isCompleted && !isActive ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                </div>
                <span
                  className={clsx(
                    'hidden md:block text-sm font-medium',
                    isActive && 'text-primary-700',
                    isCompleted && !isActive && 'text-twins-dark',
                    !isActive && !isCompleted && 'text-gray-500'
                  )}
                >
                  {step.title}
                </span>
              </button>

              {/* Connector line */}
              {index < steps.length - 1 && (
                <div
                  className={clsx(
                    'h-0.5 w-8 mx-2',
                    completedSteps.has(index) ? 'bg-twins' : 'bg-gray-200'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="card p-6 mb-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <span>Block {currentStep + 1} of {steps.length}</span>
          </div>
          <h2 className="text-xl font-semibold">{steps[currentStep].title}</h2>
          <p className="text-gray-600">{steps[currentStep].subtitle}</p>
        </div>
        <CurrentStepComponent onComplete={handleNext} />
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={currentStep === 0}
          className={clsx(
            'btn-ghost flex items-center gap-2',
            currentStep === 0 && 'invisible'
          )}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <button
          onClick={handleNext}
          className="btn-primary flex items-center gap-2"
        >
          {isLastStep ? 'Complete' : 'Next'}
          {isLastStep ? <Check className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
